import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// Module-level cache shared with HtmlText.
let _slugIndex = null;
let _promise = null;
const fetchIndex = () => {
  if (_slugIndex) return Promise.resolve(_slugIndex);
  if (_promise) return _promise;
  _promise = api
    .get("/ecosystem/slug-index")
    .then((r) => { _slugIndex = r.data || {}; return _slugIndex; })
    .catch(() => { _slugIndex = {}; return _slugIndex; });
  return _promise;
};

/**
 * React hook that returns the ecosystem name → fiche path index.
 * Returns `{}` until the first fetch resolves; consumers should re-memo
 * any HTML they emit when the index changes.
 */
export const useSlugIndex = () => {
  const [idx, setIdx] = useState(_slugIndex || {});
  useEffect(() => {
    if (_slugIndex) return;
    let cancelled = false;
    fetchIndex().then((m) => { if (!cancelled) setIdx(m); });
    return () => { cancelled = true; };
  }, []);
  return idx;
};

const SITE_ORIGIN = "dataleo.com";

const parseQ = (href) => {
  try {
    if (!href) return null;
    const u = new URL(href, "https://dataleo.com");
    if (u.pathname !== "/search") return null;
    return u.searchParams.get("q");
  } catch { return null; }
};

/**
 * Apply the Dataleo link policy to a sanitized HTML string and return the
 * mutated HTML. Operates on a detached <template> so we never touch the
 * live DOM, which lets the result flow through `dangerouslySetInnerHTML`
 * without React undoing the rewrite on the next render.
 *
 * Policy:
 *   1. `<a href="/search?q=X">` whose `X` matches a published ecosystem
 *      entity → rewritten to `<a href="/ecosystem/{kind}/{slug}">` with
 *      `follow` and same tab (preserves internal PageRank).
 *   2. Other external links → `target="_blank" rel="noopener noreferrer nofollow"`.
 *   3. Other internal links → `follow`, same tab.
 */
export const rewriteHtmlLinks = (html, slugIndex) => {
  if (typeof window === "undefined" || !html) return html;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;

  for (const a of tpl.content.querySelectorAll("a")) {
    const href = a.getAttribute("href") || "";
    let isExternal = false;
    try {
      const u = new URL(href, "https://dataleo.com");
      isExternal = u.hostname !== SITE_ORIGIN;
    } catch { isExternal = /^https?:\/\//i.test(href); }

    const q = parseQ(href);
    if (q && slugIndex) {
      const hit = slugIndex[q.trim().toLowerCase()];
      if (hit?.path) {
        a.setAttribute("href", hit.path);
        a.removeAttribute("target");
        a.removeAttribute("rel");
        a.setAttribute("data-rewritten", "ecosystem");
        continue;
      }
    }

    if (isExternal) {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer nofollow");
    } else {
      a.removeAttribute("target");
      a.removeAttribute("rel");
    }
  }
  return tpl.innerHTML;
};

/**
 * Convenience hook: returns a memoized, link-policy-rewritten HTML string
 * for a given sanitized HTML input. Re-runs when either the input or the
 * loaded slug index changes.
 */
export const useRewrittenHtml = (html) => {
  const idx = useSlugIndex();
  return useMemo(() => rewriteHtmlLinks(html, idx), [html, idx]);
};
